import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Stack, Plus, PencilSimple, TrashSimple, CaretRight, Buildings, ArrowLeft, FilePdf,
  TreeStructure,
} from "@phosphor-icons/react";

const canManageRoles = new Set(["yonetici"]);

export default function ProjectStructure() {
  const { siteId } = useParams();
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const canManage = canManageRoles.has(user?.role);

  const [site, setSite] = useState(null);
  const [phases, setPhases] = useState([]);
  const [categories, setCategories] = useState([]); // global kategori listesi
  const [loading, setLoading] = useState(true);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);

  // Phase dialog
  const [phaseDialog, setPhaseDialog] = useState(false);
  const [phaseForm, setPhaseForm] = useState({ id: null, name: "", category: "Etap", parent_id: null, order: 0, notes: "", floors_count: 0, units_count: 0 });

  // Block dialog — LEGACY (kaldırıldı, JSX'te de kullanılmıyor)

  const load = async () => {
    setLoading(true);
    try {
      const [sr, pr, cr] = await Promise.all([
        axios.get(`${API}/sites/${siteId}/project`),
        axios.get(`${API}/sites/${siteId}/phases`),
        axios.get(`${API}/phase-categories`),
      ]);
      setSite({ id: siteId, name: sr.data.site_name, code: sr.data.site_code, project: sr.data.project });
      setPhases(pr.data);
      setCategories(cr.data || []);
      if (!selectedPhaseId && pr.data.length > 0) {
        setSelectedPhaseId(pr.data[0].id);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siteId]);

  const selectedPhase = phases.find(p => p.id === selectedPhaseId);

  // --- Phase actions ---
  const openNewPhase = (parentPhase = null) => {
    // Aynı seviyedeki maksimum order'ı bul
    const parent_id = parentPhase ? parentPhase.id : null;
    const siblings = phases.filter((x) => (x.parent_id || null) === parent_id);
    const nextOrder = siblings.length + 1;
    // Kategori önerisi: parent varsa boş bırak, kök seviyede "Etap"
    const nextCategory = parentPhase ? "" : "Etap";
    setPhaseForm({
      id: null, name: "", category: nextCategory, parent_id,
      order: nextOrder, notes: "", floors_count: 0, units_count: 0,
    });
    setPhaseDialog(true);
  };
  const openEditPhase = (p) => {
    setPhaseForm({
      id: p.id, name: p.name, category: p.category || "Etap",
      parent_id: p.parent_id || null,
      order: p.order || 0, notes: p.notes || "",
      floors_count: p.floors_count || 0, units_count: p.units_count || 0,
    });
    setPhaseDialog(true);
  };
  const savePhase = async () => {
    if (!phaseForm.name.trim()) { toast.error("Ad alanı zorunlu"); return; }
    const catName = (phaseForm.category || "Etap").trim() || "Etap";
    try {
      // Yeni kategori ise global listeye kaydet (idempotent)
      const exists = categories.some(
        (c) => (c.name || "").toLowerCase() === catName.toLowerCase()
      );
      if (!exists) {
        try {
          const { data: newCat } = await axios.post(`${API}/phase-categories`, { name: catName });
          setCategories((prev) => {
            const has = prev.some((c) => c.id === newCat.id);
            return has ? prev : [...prev, newCat].sort((a, b) => a.name.localeCompare(b.name, "tr"));
          });
        } catch (_) { /* sessizce geç — asıl kayıt yine de denenecek */ }
      }
      const payload = {
        name: phaseForm.name.trim(),
        category: catName,
        parent_id: phaseForm.parent_id || null,
        order: parseInt(phaseForm.order) || 0,
        notes: phaseForm.notes.trim(),
        floors_count: parseInt(phaseForm.floors_count) || 0,
        units_count: parseInt(phaseForm.units_count) || 0,
      };
      if (phaseForm.id) {
        await axios.put(`${API}/phases/${phaseForm.id}`, payload);
        toast.success("Güncellendi");
      } else {
        const { data } = await axios.post(`${API}/sites/${siteId}/phases`, payload);
        toast.success("Eklendi");
        setSelectedPhaseId(data.id);
      }
      setPhaseDialog(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delPhase = async (p) => {
    // Alt kalem sayısını (torunlar dahil) hesapla
    const countDescendants = (id) => {
      const kids = phases.filter((x) => x.parent_id === id);
      return kids.reduce((s, k) => s + 1 + countDescendants(k.id), 0);
    };
    const desc = countDescendants(p.id);
    const descNote = desc > 0
      ? ` Ayrıca ${desc} alt kalem de birlikte silinecektir.`
      : "";
    const ok = await askConfirm({
      title: "Kalemi Sil",
      description: `"${p.name}" (${p.category || "-"}) kalemini silmek istiyor musunuz?${descNote} Bu işlem geri alınamaz.`,
      destructive: true, confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/phases/${p.id}`);
      toast.success("Silindi");
      if (selectedPhaseId === p.id) setSelectedPhaseId(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // --- Sürükle-Bırak (Reparent) ---
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null); // "__root__" veya node.id

  // dragging node'un torunları — kendini veya alt kalemine bırakma engellenir
  const draggingDescendantIds = useMemo(() => {
    if (!draggingId) return new Set();
    const s = new Set([draggingId]);
    const walk = (pid) => {
      for (const c of phases) {
        if (c.parent_id === pid && !s.has(c.id)) {
          s.add(c.id);
          walk(c.id);
        }
      }
    };
    walk(draggingId);
    return s;
  }, [draggingId, phases]);

  const reparent = async (childId, newParentId) => {
    if (!childId) return;
    const child = phases.find((p) => p.id === childId);
    if (!child) return;
    if ((child.parent_id || null) === (newParentId || null)) return;
    if (newParentId && draggingDescendantIds.has(newParentId)) {
      toast.error("Kalem, kendi alt kalemine taşınamaz");
      return;
    }
    try {
      await axios.post(`${API}/phases/${childId}/reparent`, {
        parent_id: newParentId || null,
      });
      const target = newParentId
        ? (phases.find((x) => x.id === newParentId)?.name || "kalem")
        : "Kök seviye";
      toast.success(`"${child.name}" taşındı → ${target}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const onNodeDragStart = (nodeId) => (e) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", nodeId);
    setDraggingId(nodeId);
  };
  const onNodeDragEnd = () => { setDraggingId(null); setDropTargetId(null); };
  const onNodeDragOver = (nodeId) => (e) => {
    if (!draggingId || draggingDescendantIds.has(nodeId)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTargetId !== nodeId) setDropTargetId(nodeId);
  };
  const onNodeDrop = (nodeId) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const childId = e.dataTransfer.getData("text/plain") || draggingId;
    setDraggingId(null); setDropTargetId(null);
    reparent(childId, nodeId);
  };
  const onRootDragOver = (e) => {
    if (!draggingId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTargetId !== "__root__") setDropTargetId("__root__");
  };
  const onRootDrop = (e) => {
    e.preventDefault();
    const childId = e.dataTransfer.getData("text/plain") || draggingId;
    setDraggingId(null); setDropTargetId(null);
    reparent(childId, null);
  };

  // --- Block actions ---
  // NOTE: legacy blocks CRUD kaldırıldı — artık phases (parent_id ile) tüm hiyerarşiyi
  // yönetiyor. Backend `/blocks` endpoint'leri diğer sayfalar için hala mevcut.

  // ---- Ağaç yapısı: parent_id bazlı ----
  // Doğal alfabetik sıralama — "Parsel 6, 8, 13, 15" ve "P13 A1, A2, A5, C1, C2"
  // gibi harf+sayı karışık isimleri insan algısına uygun sıralar.
  const naturalSort = (a, b) =>
    (a.name || "").localeCompare(b.name || "", "tr", {
      numeric: true, sensitivity: "base",
    });

  const tree = useMemo(() => {
    const byParent = new Map();
    for (const p of phases) {
      const pid = p.parent_id || null;
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid).push(p);
    }
    for (const arr of byParent.values()) {
      arr.sort(naturalSort);
    }
    return byParent;
  }, [phases]);
  const rows = useMemo(() => {
    const out = [];
    const walk = (nodes, depth) => {
      for (const n of nodes) {
        const children = tree.get(n.id) || [];
        out.push({ node: n, depth, childCount: children.length });
        walk(children, depth + 1);
      }
    };
    walk(tree.get(null) || [], 0);
    return out;
  }, [tree]);
  // Kategori bazında dinamik sayım — hiyerarşide kullanılan her kategori için 1 kart
  const categoryCounts = useMemo(() => {
    const counts = new Map();
    for (const p of phases) {
      const cat = (p.category || "").trim() || "Diğer";
      counts.set(cat, (counts.get(cat) || 0) + 1);
    }
    // Ağaçtaki görünme sırasına göre sırala (kök düğüm önce, sonra derinlik)
    const order = [];
    const seen = new Set();
    for (const r of rows) {
      const cat = (r.node.category || "").trim() || "Diğer";
      if (!seen.has(cat)) { seen.add(cat); order.push(cat); }
    }
    return order.map((cat) => ({ category: cat, count: counts.get(cat) || 0 }));
  }, [phases, rows]);

  return (
    <Shell
      title="Proje Yapısı"
      subtitle="Proje Yönetimi"
      hideSiteSelector
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline"
                  onClick={() => window.open(`${API}/sites/${siteId}/project-structure/export.pdf`, "_blank")}
                  data-testid="structure-export-pdf"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest h-9">
            <FilePdf size={14} className="mr-1.5 text-red-600" /> Liste PDF
          </Button>
          <Button variant="outline"
                  onClick={() => window.open(`${API}/sites/${siteId}/project-structure/tree-export.pdf`, "_blank")}
                  data-testid="structure-export-tree-pdf"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest h-9">
            <TreeStructure size={14} className="mr-1.5 text-[#1B4776]" /> Görsel Ağaç PDF
          </Button>
          <Link to="/proje/mahal-yapisi">
            <Button variant="outline" className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest h-9">
              <ArrowLeft size={14} className="mr-1.5" /> Proje Listesi
            </Button>
          </Link>
        </div>
      }
    >
      {/* Üst özet band — Proje + kategori bazında dinamik sayaçlar */}
      {site && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="structure-stats">
          <Card className="p-4 rounded-none sm:rounded-sm border-l-4 border-[#95C11F]">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Proje</div>
            <div className="text-lg font-bold text-slate-900 mt-1 truncate">{site.name}</div>
            <div className="text-xs text-slate-500 font-mono">{site.code}</div>
          </Card>
          {categoryCounts.length === 0 ? (
            <Card className="p-4 rounded-none sm:rounded-sm md:col-span-3 flex items-center justify-center text-xs text-slate-400 italic">
              Henüz kalem eklenmedi — kategori bazlı sayaçlar burada görünecek
            </Card>
          ) : (
            categoryCounts.map((c, idx) => (
              <Card
                key={c.category}
                data-testid={`stat-cat-${c.category}`}
                className="p-4 rounded-none sm:rounded-sm"
              >
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 truncate">
                  Toplam {c.category}
                </div>
                <div className={`text-3xl font-bold mt-1 font-heading ${idx === 0 ? "text-[#1B4776]" : "text-[#95C11F]"}`}>
                  {c.count}
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : phases.length === 0 ? (
        /* ---------- Boş durum: Ana Mahal Yapısını Belirle CTA ---------- */
        <Card className="rounded-none sm:rounded-sm border-2 border-dashed border-slate-300 bg-slate-50/50 py-16 px-6 text-center" data-testid="empty-structure">
          <div className="mx-auto w-16 h-16 rounded-full bg-white flex items-center justify-center border border-slate-200 mb-4">
            <Stack size={32} weight="duotone" className="text-[#95C11F]" />
          </div>
          <div className="text-lg font-bold text-slate-900">
            Henüz proje yapısı oluşturulmadı
          </div>
          <div className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Projenizin ana mahal yapısını (Etap / Ada / Kısım / Bölüm / Blok…) manuel olarak oluşturun.
            Kategori serbesttir, siz belirlersiniz — her kalemin altına dilediğiniz kadar alt kalem ekleyebilirsiniz.
          </div>
          {canManage && (
            <Button
              onClick={() => openNewPhase(null)}
              data-testid="create-structure-btn"
              className="mt-6 rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white h-11 px-6"
            >
              <Plus size={16} className="mr-2" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold">
                Ana Mahal Yapısını Belirle
              </span>
            </Button>
          )}
        </Card>
      ) : (
        /* ---------- Hiyerarşik Ağaç Görünümü (özyinelemeli) ---------- */
        <Card className="rounded-none sm:rounded-sm border-slate-200" data-testid="structure-tree">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 flex items-center gap-2">
              <Stack size={16} weight="duotone" className="text-[#95C11F]" />
              Proje Hiyerarşisi
              {canManage && (
                <span className="ml-2 hidden md:inline text-[9px] normal-case tracking-normal font-normal text-slate-500 italic">
                  · Kalemi başka bir kalem üzerine sürükleyerek alt kalem yapabilirsiniz
                </span>
              )}
            </div>
            {canManage && (
              <Button onClick={() => openNewPhase(null)} data-testid="new-phase-btn"
                      size="sm" className="h-8 rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                <Plus size={12} className="mr-1" />
                <span className="text-[10px] uppercase tracking-widest font-bold">Ana Kalem Ekle</span>
              </Button>
            )}
          </div>
          <div className="p-2">
            {rows.map((r) => (
              <TreeNode
                key={r.node.id}
                node={r.node}
                childCount={r.childCount}
                depth={r.depth}
                canManage={canManage}
                onAddChild={openNewPhase}
                onEdit={openEditPhase}
                onDelete={delPhase}
                draggingId={draggingId}
                dropTargetId={dropTargetId}
                isForbiddenTarget={draggingDescendantIds.has(r.node.id)}
                onDragStart={onNodeDragStart(r.node.id)}
                onDragEnd={onNodeDragEnd}
                onDragOver={onNodeDragOver(r.node.id)}
                onDragLeave={() => setDropTargetId((cur) => (cur === r.node.id ? null : cur))}
                onDrop={onNodeDrop(r.node.id)}
              />
            ))}
            {canManage && draggingId && (
              <div
                data-testid="phase-root-dropzone"
                onDragOver={onRootDragOver}
                onDragLeave={() => setDropTargetId((cur) => (cur === "__root__" ? null : cur))}
                onDrop={onRootDrop}
                className={`mt-3 px-4 py-4 border-2 border-dashed text-center text-[10px] uppercase tracking-widest font-bold transition-colors ${
                  dropTargetId === "__root__"
                    ? "border-[#95C11F] bg-[#F7FBE9] text-[#1B4776]"
                    : "border-slate-300 text-slate-500 bg-slate-50"
                }`}
              >
                ⇧ Kök seviyeye taşımak için buraya bırakın
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ---------- Görsel Ağaç (Organizasyon Şeması) ---------- */}
      {phases.length > 0 && (
        <Card className="rounded-none sm:rounded-sm border-slate-200 mt-6" data-testid="structure-chart">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 flex items-center gap-2">
              <Stack size={16} weight="duotone" className="text-[#1B4776]" />
              Görsel Ağaç
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Yatay kaydırılabilir · Zoom için CTRL + fare tekerleği
            </div>
          </div>
          <div className="p-6 overflow-x-auto bg-slate-50/50">
            <HierarchyChart tree={tree} rootId={null}
                            projectLabel={site?.name || "Proje"}
                            projectCode={site?.code || ""} />
          </div>
        </Card>
      )}

      {/* Etap Dialog */}
      <Dialog open={phaseDialog} onOpenChange={setPhaseDialog}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight font-heading">
              {phaseForm.id ? "Ana Kalemi Düzenle" : "Ana Mahal Kalemi Ekle"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Kategoriyi (Etap / Ada / Kısım / Bölüm / Blok…) serbestçe belirleyip
              adıyla birlikte tanımlayın.
            </DialogDescription>
          </DialogHeader>
          <datalist id="phase-category-suggestions">
            {categories.map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
          <div className="space-y-4">
            {phaseForm.parent_id && (() => {
              const parent = phases.find(x => x.id === phaseForm.parent_id);
              return parent ? (
                <div className="bg-slate-50 border border-slate-200 px-3 py-2 rounded-sm flex items-center gap-2"
                     data-testid="phase-form-parent-banner">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#1B4776]/10 text-[9px] uppercase tracking-widest font-bold text-[#1B4776]">
                    {parent.category || "Etap"}
                  </span>
                  <span className="text-xs text-slate-500">Alt kalem olarak:</span>
                  <span className="text-sm font-bold text-slate-900 truncate">{parent.name}</span>
                </div>
              ) : null;
            })()}
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Kategori</Label>
              <Input value={phaseForm.category}
                     data-testid="phase-form-category"
                     list="phase-category-suggestions"
                     onChange={e => setPhaseForm({...phaseForm, category: e.target.value})}
                     placeholder="Etap / Ada / Kısım / Bölüm / Blok…"
                     className="mt-2 rounded-none sm:rounded-sm" />
              <div className="text-[10px] text-slate-500 mt-1">
                Listeden seçebilir veya kendi kategorinizi yazabilirsiniz — yazdıklarınız kalıcı olarak kaydedilir ve tüm projelerde tekrar kullanılır.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Adı / Kodu</Label>
              <Input value={phaseForm.name}
                     data-testid="phase-form-name"
                     onChange={e => setPhaseForm({...phaseForm, name: e.target.value})}
                     placeholder="Örn: 1. Ada, A Etabı, Batı Kanat"
                     className="mt-2 rounded-none sm:rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhaseDialog(false)}
                    className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={savePhase} data-testid="phase-form-save"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              {phaseForm.id ? "Güncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}


// ============================================================================
// TreeNode — özyinelemeli hiyerarşi düğümü
// ============================================================================
function TreeNode({
  node, childCount = 0, depth, canManage, onAddChild, onEdit, onDelete,
  draggingId, dropTargetId, isForbiddenTarget,
  onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
}) {
  const hasChildren = childCount > 0;
  const indentPx = depth * 20;
  const isDragging = draggingId === node.id;
  const isDropTarget = dropTargetId === node.id && !isForbiddenTarget;

  return (
    <div
      className="select-none"
      data-testid={`phase-node-${node.id}`}
      draggable={canManage ? "true" : "false"}
      onDragStart={canManage ? onDragStart : undefined}
      onDragEnd={canManage ? onDragEnd : undefined}
      onDragOver={canManage ? onDragOver : undefined}
      onDragLeave={canManage ? onDragLeave : undefined}
      onDrop={canManage ? onDrop : undefined}
    >
      <div
        className={`flex items-center gap-2 px-2 py-2 rounded-sm hover:bg-slate-50 group relative transition-all ${
          isDragging ? "opacity-40" : ""
        } ${isDropTarget ? "bg-[#F7FBE9] ring-2 ring-[#95C11F]" : ""} ${
          isForbiddenTarget && draggingId ? "opacity-40 cursor-not-allowed" : ""
        } ${canManage ? "cursor-grab active:cursor-grabbing" : ""}`}
        style={{ paddingLeft: 8 + indentPx }}
      >
        {/* Ağaç çizgisi (derinlik > 0 için) */}
        {depth > 0 && (
          <span
            className="absolute left-0 top-0 bottom-0 border-l border-slate-200"
            style={{ left: 8 + (depth - 1) * 20 + 10 }}
            aria-hidden
          />
        )}
        <Stack
          size={16}
          weight="duotone"
          className={depth === 0 ? "text-[#1B4776] flex-shrink-0" : "text-[#95C11F] flex-shrink-0"}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#1B4776]/10 text-[9px] uppercase tracking-widest font-bold text-[#1B4776]">
              {node.category || "-"}
            </span>
            <span className="font-bold text-slate-900 truncate">{node.name}</span>
            {hasChildren && (
              <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500">
                {childCount} alt kalem
              </span>
            )}
            {(node.floors_count > 0 || node.units_count > 0) && (
              <span className="text-[10px] font-mono text-slate-600">
                {node.floors_count > 0 && <>Kat: <b className="text-[#1B4776]">{node.floors_count}</b> </>}
                {node.units_count > 0 && <>· BB: <b className="text-[#95C11F]">{node.units_count}</b></>}
              </span>
            )}
          </div>
          {node.notes && (
            <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{node.notes}</div>
          )}
        </div>
        {canManage && (
          <div className="flex gap-1 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onAddChild(node)}
              data-testid={`add-child-${node.id}`}
              title="Alt Bölüm Ekle"
              className="h-7 px-2 flex items-center gap-1 border border-[#95C11F] bg-[#95C11F] hover:bg-[#7ea11a] text-[#1B2A3A] text-[10px] uppercase tracking-widest font-bold"
            >
              <Plus size={11} /> Alt Bölümler
            </button>
            <button
              onClick={() => onEdit(node)}
              data-testid={`edit-phase-${node.id}`}
              title="Kalemi Düzenle"
              className="w-7 h-7 flex items-center justify-center text-[#1B4776] hover:bg-slate-100"
            >
              <PencilSimple size={12} />
            </button>
            <button
              onClick={() => onDelete(node)}
              data-testid={`del-phase-${node.id}`}
              title="Kalemi Sil"
              className="w-7 h-7 flex items-center justify-center text-red-600 hover:bg-red-50"
            >
              <TrashSimple size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// HierarchyChart — Görsel organizasyon şeması (tek komponent, iç recursion)
// visual-edits babel plugin'i self-referencing komponentlerde patlar; bu yüzden
// düğüm render'ı iç kapalı fonksiyon (renderNode) olarak yazıldı.
// ============================================================================
function HierarchyChart({ tree, rootId, projectLabel, projectCode }) {
  const CAT_COLORS = {
    Etap: "#1B4776", Ada: "#2563eb", Blok: "#95C11F",
    Kısım: "#B45309", Bölüm: "#8b5cf6", Faz: "#0891b2", Bina: "#dc2626",
  };
  // Zoom seviyesi — WBS ile birebir aynı davranış
  const [zoom, setZoom] = React.useState(1.0);
  const ZOOM_STEPS = [0.25, 0.5, 0.75, 1.0, 1.25];
  const renderNode = (node) => {
    const children = tree.get(node.id) || [];
    const hasChildren = children.length > 0;
    const accent = CAT_COLORS[node.category] || "#475569";
    // Kural: sadece TÜM çocuklar YAPRAK ise dikey 2-sütun layout;
    // branch'lerde yatay layout. WBS ile birebir aynı kural.
    const childrenAreAllLeaves = hasChildren && children.every(
      (c) => (tree.get(c.id) || []).length === 0
    );
    const useVertical = childrenAreAllLeaves;
    const ulCls = "hc-children" + (useVertical ? " hc-children-vertical" : "");
    return (
      <li className="hc-node" key={node.id} data-testid={`chart-node-${node.id}`}>
        <div className="hc-box" style={{ borderTopColor: accent }}>
          <div className="hc-cat" style={{ backgroundColor: accent }}>
            {node.category || "-"}
          </div>
          <div className="hc-name" title={node.name}>{node.name}</div>
          {hasChildren && (
            <div className="hc-count">{children.length} alt bölüm</div>
          )}
        </div>
        {hasChildren && (
          <ul className={ulCls}>
            {children.map(renderNode)}
          </ul>
        )}
      </li>
    );
  };

  const roots = tree.get(rootId ?? null) || [];
  if (roots.length === 0) {
    return <div className="text-xs text-slate-400 text-center py-8">Görüntülenecek kalem yok.</div>;
  }
  return (
    <>
      <style>{`
        .hc-tree { display: inline-flex; padding-top: 0; }
        .hc-tree ul { display: flex; position: relative; margin: 0; list-style: none; padding-left: 0; padding-top: 0; }
        .hc-tree ul.hc-children { padding-top: 28px; }
        .hc-tree li { list-style: none; text-align: center; padding: 0 12px; position: relative; }
        .hc-tree ul.hc-children > li { padding-top: 28px; }
        /* Kardeşler arası yatay + dikey bağlantı — SADECE alt (nested) seviyeler için */
        .hc-tree ul.hc-children > li::before,
        .hc-tree ul.hc-children > li::after {
          content: "";
          position: absolute;
          top: 0;
          height: 28px;
          border-top: 2px solid #94a3b8;
        }
        .hc-tree ul.hc-children > li::before { right: 50%; left: 0; }
        .hc-tree ul.hc-children > li::after  { left: 50%; right: 0; }
        .hc-tree ul.hc-children > li:only-child::before,
        .hc-tree ul.hc-children > li:only-child::after { display: none; }
        .hc-tree ul.hc-children > li:first-child::before,
        .hc-tree ul.hc-children > li:last-child::after { border: 0 none; }
        .hc-tree ul.hc-children > li:last-child::before  { border-right: 2px solid #94a3b8; border-radius: 0 6px 0 0; }
        .hc-tree ul.hc-children > li:first-child::after  { border-left:  2px solid #94a3b8; border-radius: 6px 0 0 0; }
        /* Merkezden yukarı çıkan dikey çizgi — SADECE nested ul için */
        .hc-tree ul.hc-children::before {
          content: "";
          position: absolute;
          top: 0;
          left: 50%;
          width: 0;
          height: 28px;
          border-left: 2px solid #94a3b8;
        }
        /* DİKEY YAPRAK LAYOUT — çocuklar iki sütun (grid) halinde dizilir.
           Parent'tan dikey trunk çıkar, iki yana ayrılır; her sütunun tepesine
           bir bağlantı gider. Sütun içindeki elemanlar alt alta stack olur. */
        .hc-tree ul.hc-children-vertical {
          display: grid !important;
          grid-template-columns: 1fr 1fr;
          column-gap: 64px; row-gap: 12px;
          padding: 44px 0 0 0;
          justify-items: center;
        }
        /* Base horizontal T-connectors'ı vertical layoutta iptal et */
        .hc-tree ul.hc-children-vertical > li::before,
        .hc-tree ul.hc-children-vertical > li::after {
          content: none !important;
        }
        /* Dikey trunk (parent'ın altından iniyor) */
        .hc-tree ul.hc-children-vertical::before {
          content: ""; position: absolute;
          top: 0; left: 50%;
          width: 0; height: 22px;
          border-left: 2px solid #94a3b8;
        }
        /* Ortadaki yatay ayırıcı — sol yarıdan sağ yarıya, iki sütunun
           merkezini birleştirir (grid 2 sütunun ortaları 25% ve 75%). */
        .hc-tree ul.hc-children-vertical::after {
          content: ""; position: absolute;
          top: 22px; left: 25%; right: 25%;
          height: 0;
          border-top: 2px solid #94a3b8;
        }
        .hc-tree ul.hc-children-vertical > li {
          padding: 0; position: relative;
          text-align: center;
        }
        /* Her sütunun ilk yaprağı: üstünden yatay ayırıcıya bir dikey çizgi */
        .hc-tree ul.hc-children-vertical > li:nth-child(-n+2) {
          position: relative;
        }
        .hc-tree ul.hc-children-vertical > li:nth-child(-n+2)::before {
          content: "" !important;
          position: absolute;
          left: 50%; top: -22px;
          width: 0; height: 22px;
          border-left: 2px solid #94a3b8;
          border-radius: 0;
          background: none;
        }
        /* Tek çocuklu durumda ayırıcıyı gizle — trunk direkt tek çocuğa gider */
        .hc-tree ul.hc-children-vertical:has(> li:only-child)::after { display: none; }
        .hc-tree ul.hc-children-vertical:has(> li:only-child) {
          grid-template-columns: 1fr;
        }
        /* Kart görünümü */
        .hc-box {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          min-width: 140px;
          max-width: 200px;
          padding: 8px 12px 10px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-top: 4px solid #475569;
          border-radius: 4px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
          position: relative;
        }
        .hc-box-root {
          min-width: 220px;
          max-width: 320px;
          padding: 10px 16px 12px;
          border: 1.5px solid #1B4776;
          border-top-width: 6px;
          background: linear-gradient(180deg, #ffffff 0%, #F1F5F9 100%);
        }
        .hc-box-root .hc-name { font-size: 14px; }
        .hc-box:hover {
          box-shadow: 0 4px 10px rgba(15, 23, 42, 0.10);
          transform: translateY(-1px);
          transition: all 120ms ease;
        }
        .hc-cat {
          font-size: 8px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-weight: 800;
          color: #ffffff;
          padding: 2px 8px;
          border-radius: 2px;
          margin-bottom: 6px;
          white-space: nowrap;
        }
        .hc-name {
          font-size: 12px;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.15;
          word-wrap: break-word;
        }
        .hc-count {
          font-size: 9px;
          font-weight: 700;
          color: #64748b;
          margin-top: 4px;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }
      `}</style>
      {/* Zoom toolbar — WBS ile birebir aynı davranış */}
      <div className="flex items-center gap-1.5 mb-3 sticky top-0 z-10 bg-white/95 backdrop-blur py-2 border-b border-slate-200"
           data-testid="cbs-zoom-toolbar">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-2">
          Yakınlaştır
        </span>
        {ZOOM_STEPS.map((z) => (
          <button
            key={z}
            type="button"
            onClick={() => setZoom(z)}
            data-testid={`cbs-zoom-${Math.round(z * 100)}`}
            className={`px-2.5 py-1 text-[11px] font-bold border transition-colors ${
              Math.abs(zoom - z) < 0.001
                ? "bg-[#1B4776] text-white border-[#1B4776]"
                : "bg-white text-[#1B4776] border-slate-300 hover:border-[#1B4776]"
            }`}
          >
            %{Math.round(z * 100)}
          </button>
        ))}
        <span className="text-[10px] text-slate-400 ml-2">
          İpucu: CTRL + fare tekerleği ile de yakınlaştırabilirsiniz
        </span>
      </div>
      <div
        className="hc-tree"
        style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
      >
        <ul>
          <li className="hc-node" data-testid="chart-node-project">
            <div className="hc-box hc-box-root" style={{ borderTopColor: "#1B4776" }}>
              <div className="hc-cat" style={{ backgroundColor: "#1B4776" }}>
                Proje
              </div>
              <div className="hc-name" title={projectLabel}>{projectLabel}</div>
              {projectCode && (
                <div className="hc-count" style={{ color: "#95C11F" }}>{projectCode}</div>
              )}
            </div>
            {roots.length > 0 && (
              <ul className="hc-children">
                {roots.map(renderNode)}
              </ul>
            )}
          </li>
        </ul>
      </div>
    </>
  );
}
